import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase F.5 Batch 14b — Admin Auditing Tool (YoY comparisons). */
export default function AuditYoY() {
  const now = new Date().getFullYear();
  const [yearA, setYearA] = useState(now - 1);
  const [yearB, setYearB] = useState(now);
  const [years, setYears] = useState([now, now - 1, now - 2]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadYears = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/audit/years`);
      const ys = r.data?.years || [];
      if (ys.length) setYears(ys);
    } catch (e) { /* */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit/yoy`, { params: { year_a: yearA, year_b: yearB } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setLoading(false); }
  }, [yearA, yearB]);

  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { load(); }, [load]);

  const fmt = (v, isMoney) => {
    if (v === null || v === undefined) return "—";
    if (typeof v !== "number") return String(v);
    return isMoney ? `$${v.toFixed(2)}` : v.toLocaleString();
  };

  const renderDelta = (d) => {
    if (!d) return null;
    if (d.delta_abs === 0) return <span className="text-muted-foreground flex items-center gap-1"><Minus className="h-3 w-3" /> no change</span>;
    const up = d.delta_abs > 0;
    return (
      <span className={`flex items-center gap-1 text-xs ${up ? "text-emerald-700" : "text-rose-600"}`}>
        {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {Math.abs(d.delta_abs).toLocaleString()} ({d.delta_pct > 0 ? "+" : ""}{d.delta_pct}%)
      </span>
    );
  };

  const renderSection = (title, sec, rows) => (
    <Card data-testid={`yoy-section-${title.toLowerCase()}`}>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-1 pr-2">Metric</th>
              <th className="py-1 px-2">{yearA}</th>
              <th className="py-1 px-2">{yearB}</th>
              <th className="py-1 pl-2">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, label, isMoney]) => (
              <tr key={key} className="border-b last:border-b-0" data-testid={`yoy-row-${key}`}>
                <td className="py-2 pr-2 font-medium">{label}</td>
                <td className="py-2 px-2">{fmt(sec.year_a[key], isMoney)}</td>
                <td className="py-2 px-2">{fmt(sec.year_b[key], isMoney)}</td>
                <td className="py-2 pl-2">{renderDelta(sec.diff[key])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 p-4" data-testid="audit-yoy-page">
      <div>
        <h1 className="text-2xl font-bold">Year-over-Year Audit</h1>
        <p className="text-sm text-muted-foreground">
          Compare two years across financials, inventory, training, and safety.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4 flex gap-4 items-end flex-wrap">
          <div>
            <Label>Baseline year (A)</Label>
            <Select value={String(yearA)} onValueChange={(v) => setYearA(Number(v))}>
              <SelectTrigger className="w-40" data-testid="yoy-year-a"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Compare to (B)</Label>
            <Select value={String(yearB)} onValueChange={(v) => setYearB(Number(v))}>
              <SelectTrigger className="w-40" data-testid="yoy-year-b"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="yoy-run">
            {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Run audit
          </Button>
        </CardContent>
      </Card>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderSection("Financials", data.sections.financials, [
            ["quotes_count", "Quotes created", false],
            ["quotes_total", "Quotes total", true],
            ["invoices_count", "Invoices created", false],
            ["invoices_revenue", "Revenue collected", true],
            ["invoices_outstanding", "Outstanding (this year)", true],
          ])}
          {renderSection("Inventory", data.sections.inventory, [
            ["items_added", "Items added", false],
            ["items_added_active", "Active items added", false],
            ["total_items_active_now", "Total active (current)", false],
            ["low_stock_now", "Low-stock now", false],
            ["stock_value_now", "Stock value (current)", true],
          ])}
          {renderSection("Training", data.sections.training, [
            ["programs_created", "Programs created", false],
            ["assignments", "Assignments", false],
            ["completions", "Completions", false],
            ["completion_rate", "Completion %", false],
          ])}
          {renderSection("Safety", data.sections.safety, [
            ["docs_created", "Safety docs created", false],
          ])}
        </div>
      )}
    </div>
  );
}
